import * as React from 'react';
import { Box, Button, type GDSStyleObject } from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import { useVirtualizer, type VirtualItem } from '@tanstack/react-virtual';

// Pixel value because scroll metrics (scrollHeight, scrollTop, clientHeight) are in px
const DEFAULT_BOTTOM_THRESHOLD = 50;
const DEFAULT_OVERSCAN = 5;

export interface VirtualizedAutoScrollBoxRenderProps {
  virtualItems: VirtualItem[];
  totalSize: number;
  measureElement: (element: HTMLElement | null) => void;
}

export type VirtualizedAutoScrollBoxProps = {
  /**
   * Render function called with the virtualizer's current state.
   * Use the provided `virtualItems`, `totalSize`, and `measureElement`
   * to render and position only the visible items.
   */
  children: (
    renderProps: VirtualizedAutoScrollBoxRenderProps,
  ) => React.ReactNode;
  /**
   * Distance in pixels from the bottom edge within which the container
   * is considered "at the bottom" for auto-scroll purposes.
   * @default 50
   */
  threshold?: number;
  /** Custom CSS styles for the outer container */
  css?: GDSStyleObject;
  /** Total number of items to virtualize */
  itemCount: number;
  /**
   * Returns the estimated height in pixels for a given item index.
   * If using dynamic measurement via `measureElement`, estimate the largest
   * expected size for best initial layout accuracy.
   */
  estimateSize: (index: number) => number;
  /**
   * Returns a stable unique key for the item at the given index.
   * Defaults to the item index. Override when items have natural IDs.
   */
  getItemKey?: (index: number) => string | number;
  /**
   * When true, automatically scrolls to the bottom as new items are added
   * (while the user is already at/near the bottom). When false the list
   * stays at the current scroll position as items stream in.
   * @default false
   */
  autoScroll?: boolean;
  /**
   * Number of items to render above and below the visible area.
   * Higher values reduce blank-flash risk at the cost of more DOM nodes.
   * @default 5
   */
  overscan?: number;
} & (
  | {
      /** Accessible label for the scrollable region. Use when no visible label exists. */
      ariaLabel: string;
      ariaLabelledBy?: never;
    }
  | {
      ariaLabel?: never;
      /** ID of an element that labels the scrollable region. Prefer this when visible label text exists. */
      ariaLabelledBy: string;
    }
);

/**
 * A virtualized scrollable container with auto-scroll-to-bottom behaviour.
 *
 * Only the visible items (plus overscan) are mounted in the DOM, making it
 * suitable for lists with thousands of items.
 *
 * Key behaviours:
 * - When `autoScroll` is enabled, scrolls to bottom as `itemCount` grows if user is at/near the bottom
 * - Pauses auto-scroll when user scrolls up (respects user intent)
 * - Resumes auto-scroll when user scrolls back to bottom
 * - Shows a "Scroll to Bottom" button when content overflows and user is not at the bottom
 *
 * The `children` render prop gives consumers full control over the inner DOM
 * structure, so it works with `<table>`, plain `<div>` lists, or any layout.
 */
export const VirtualizedAutoScrollBox = ({
  itemCount,
  estimateSize,
  children,
  getItemKey,
  autoScroll = false,
  overscan = DEFAULT_OVERSCAN,
  threshold = DEFAULT_BOTTOM_THRESHOLD,
  css: cssProp,
  ariaLabel,
  ariaLabelledBy,
}: VirtualizedAutoScrollBoxProps) => {
  const { formatMessage } = useIntl();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = React.useState(true);

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => containerRef.current,
    estimateSize,
    overscan,
    getItemKey,
  });

  const scrollToBottom = React.useCallback((behavior: ScrollBehavior) => {
    const container = containerRef.current;
    if (!container) return;

    if (typeof container.scrollTo === 'function') {
      container.scrollTo({ top: container.scrollHeight, behavior });
      return;
    }

    container.scrollTop = container.scrollHeight;
  }, []);

  /**
   * Check if the container is scrolled to/near the bottom
   */
  const checkIfAtBottom = React.useCallback(() => {
    const container = containerRef.current;
    if (!container) return true;

    const { scrollHeight, scrollTop, clientHeight } = container;
    // Use a small buffer so sub-pixel/rounding differences do not disable
    // auto-scroll when the user is visually at the bottom.
    return scrollHeight - scrollTop - clientHeight <= threshold;
  }, [threshold]);

  /**
   * Handle scroll events to track if user has scrolled away from bottom
   */
  const handleScroll = React.useCallback(() => {
    setIsAtBottom(checkIfAtBottom());
  }, [checkIfAtBottom]);

  /**
   * Handle clicking the "Scroll to Bottom" button
   */
  const handleScrollToBottomClick = React.useCallback(() => {
    scrollToBottom('auto');
    setIsAtBottom(true);
  }, [scrollToBottom]);

  /**
   * When itemCount changes, re-check scroll position so the "Scroll to Bottom"
   * button appears if items overflow beyond the visible area.
   * Only auto-scroll to the bottom when `autoScroll` is enabled and the user
   * is already at/near the bottom; otherwise, just update the bottom state.
   */
  React.useEffect(() => {
    if (autoScroll && isAtBottom) {
      scrollToBottom('auto');
    } else {
      setIsAtBottom(checkIfAtBottom());
    }
  }, [itemCount, autoScroll, isAtBottom, scrollToBottom, checkIfAtBottom]);

  const renderProps: VirtualizedAutoScrollBoxRenderProps = {
    virtualItems: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
    measureElement: virtualizer.measureElement,
  };

  return (
    <Box
      css={{
        position: 'relative',
        height: '100%',
        backgroundColor: '$surfaceBackgroundSecondary',
        border: '$borderWidths$thin solid $onSurfaceBorderSubdued',
        borderRadius: '$medium',
        overflow: 'hidden',
        ...cssProp,
      }}
    >
      <Box
        ref={containerRef}
        onScroll={handleScroll}
        role="region"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        css={{
          height: '100%',
          overflowY: 'auto',
          borderRadius: 'inherit',
        }}
      >
        {children(renderProps)}
      </Box>

      {!isAtBottom && (
        <Box
          css={{
            position: 'absolute',
            insetBlockEnd: '$one',
            insetInlineStart: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        >
          <Button
            size="small"
            priority="tertiary"
            onClick={handleScrollToBottomClick}
            css={{ backgroundColor: '$surfaceBackgroundPrimary' }}
          >
            {formatMessage({ id: 'SCROLL_TO_BOTTOM' })}
          </Button>
        </Box>
      )}
    </Box>
  );
};
